import { AxiosError } from 'axios';
import Axios from '@/utils/axios';
import { SandboxWebhookDelivery } from '@/services/api/sandbox';

// ─── Webhook deliveries, live or sandbox ──────────────────────────────
//   GET  /v1/integration/shipments/webhook-deliveries   shipments + latest webhook
//   GET  /v1/integration/shipments/:id/webhook-deliveries  one shipment's webhooks
//   POST /v1/integration/shipments/:id/webhook/retry    send the latest again
// The portal login picks the environment with X-Api-Mode — the same calls a
// partner makes with its dash_live_ / dash_test_ key.
export type WebhookMode = 'live' | 'sandbox';

const headersOf = (mode: WebhookMode) => ({
  'X-Channel': 'LINK',
  'X-Api-Mode': mode,
});

/** `failing` = the latest webhook did not arrive (and is not queued). */
export type ShipmentWebhooksFilter = 'failing' | 'all';

export interface ShipmentWebhookRow {
  shipment: { id: string; waybill: string; bookingId: string; status: string };
  /** Its latest webhook (the copy on the active endpoint when there is one). */
  latest: SandboxWebhookDelivery;
  /** The retry would be accepted now. */
  retryable: boolean;
}

export interface ShipmentWebhooksPage {
  data: ShipmentWebhookRow[];
  pagination: { size: number; page: number; lastPage: number; total: number };
}

export const listShipmentWebhooks = async (
  mode: WebhookMode,
  query: {
    status: ShipmentWebhooksFilter;
    search?: string;
    page: number;
    limit: number;
  }
): Promise<ShipmentWebhooksPage> => {
  const res = await Axios.get<{ data: ShipmentWebhooksPage }>(
    '/v1/integration/shipments/webhook-deliveries',
    {
      headers: headersOf(mode),
      params: { ...query, search: query.search?.trim() || undefined },
    }
  );
  return res.data.data;
};

export const listShipmentWebhookDeliveries = async (
  mode: WebhookMode,
  shipmentId: string
): Promise<SandboxWebhookDelivery[]> => {
  const res = await Axios.get<{ data: { data: SandboxWebhookDelivery[] } }>(
    `/v1/integration/shipments/${shipmentId}/webhook-deliveries`,
    { headers: headersOf(mode) }
  );
  return res.data.data.data;
};

/** The API's answer, refusals included (409 / 429 / 400 are expected). */
export const retryShipmentWebhook = async (
  mode: WebhookMode,
  shipmentId: string
): Promise<{ status: number; response: unknown }> => {
  try {
    const res = await Axios.post(
      `/v1/integration/shipments/${shipmentId}/webhook/retry`,
      {},
      { headers: headersOf(mode) }
    );
    return { status: res.status, response: res.data };
  } catch (err) {
    const response = (err as AxiosError).response;
    if (response) return { status: response.status, response: response.data };
    throw err;
  }
};
